<template>
  <div class="account-login">
    <el-form
      ref="loginForm"
      :model="loginForm"
      :rules="loginRules"
      class="login-form"
      label-position="left"
      label-width="24px"
      content-width="350px"
    >
      <el-form-item prop="email" ref="emailFormItem">
        <template #label>
          <i class="icomoon-common_user" />
        </template>
        <el-input
          :tips="$t('v_email')"
          :placeholder="$t('e-mail')"
          v-model.trim="loginForm.email"
          @keypress.enter.native="onLogin"
          :maxlength="128"
          @change="emailChange"
        ></el-input>
      </el-form-item>
      <el-form-item prop="password" ref="passwordFormItem">
        <template #label>
          <i class="icomoon-common_lock" />
        </template>
        <div>
          <cloud-password
            :placeholder="$t('password')"
            v-model.trim="loginForm.password"
            @keypress.enter.native="onLogin"
            :maxlength="32"
            force-show-eyes
          ></cloud-password>
        </div>
      </el-form-item>
      <el-form-item prop="verifyCode" v-if="needVerifyCode" ref="verifyCodeFormItem">
        <template #label>
          <i class="icomoon-common_lock" />
        </template>
        <el-input
          v-model.trim="loginForm.verifyCode"
          :placeholder="$t('enter_img_verify_code')"
          class="login-code-input"
          v-input-limit
          data-input-type="alphanumeric"
          :maxlength="4"
          @keypress.enter.native="onLogin"
        ></el-input>
        <img :src="verifyImg" alt @click="changeCode" class="login-code-pic" crossorigin="anonymous">
      </el-form-item>
    </el-form>
    <div class="auto-content">
      <el-checkbox style="vertical-align: middle" v-model="rememberLogin">
        {{ $t('HPP-web_remember_login_msg', { 0: '7' }) }}
      </el-checkbox>
      <div v-if="rememberLogin">{{ $t('HPP-web_remember_login_warn_msg') }}</div>
    </div>

    <el-button type="link" class="password-button" @click="toReset()" :title="$t('forgot_password')">
      {{ $t('forgot_password') }}
    </el-button>
    <div class="entry-button-warp">
      <el-button :title="$t('login')" @click="onLogin" class="entry-button" type="primary">
        {{ $t('login') }}
      </el-button>
      <t class="to-onehiklogin" lang="upgraded_please_use" tag="span" v-if="showOneHik">
        <button class="el-button el-button--link" @click="toOneHikIdLogin"></button>
      </t>
    </div>
    <cloud-dialog v-model="resendModel" class="login-resend-dialog">
      <div class="dialog-info-position">
        <i class="icon-img_information"></i>
      </div>
      <div class="dialog-content">
        <div class="dialog-title">{{ $t('confirm') }}</div>
        <div class="dialog-text">
          <p class="dialog-tip">{{ $t('LAP001015').replace('{0}', loginForm.email) }}</p>
          <div>
            <span>{{ $t('never_receive_email') }}</span>
            <el-button type="link" @click="handleResend">{{ $t('resend') }}</el-button>
          </div>
        </div>
      </div>
      <el-button type="primary" class="close-button" @click="resendModel = false">
        {{ $t('haven_active') }}
      </el-button>
    </cloud-dialog>
    <authentication-entry-dialog
      :login-form="{ loginForm }"
      :visible.sync="showAuthentication"
      @confirm="handleAuthenticationFn.resolve && handleAuthenticationFn.resolve()"
    />
  </div>
</template>
<script>
import { isAccountNeedUpgrade, getOneHikIdUpgradeStrategy } from '@pro/common/dao/upgrade-strategy.js';
import CloudPassword from '@/Common/components/cloud-password';
import CloudDialog from '@/Common/components/cloud-no-header-dialog';
import cryptoRSA from '@pro/common/utils/cryptoRSA';
import cryptoAES from '@pro/common/utils/cryptoAES';

import { handleFingerPrint } from '@/Common/libs/utils';
import AuthenticationEntryDialog from '@pro/common/components/2FA/double-authentication-entry-dialog.vue';

export default {
  name: 'AccountLogin',
  components: {
    CloudPassword,
    CloudDialog,
    AuthenticationEntryDialog
  },
  props: {
    autoEmail: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      needVerifyCode: false,
      loginForm: {
        email: sessionStorage.getItem('accountEmailCache') || '',
        password: '',
        verifyCode: ''
      },
      resendModel: false, // 账号邮箱未校验
      verifyImg: '',
      rememberLogin: false,
      showAuthentication: false,
      handleAuthenticationFn: {
        resolve: null,
        reject: null
      }
    };
  },
  computed: {
    loginRules() {
      return {
        email: [
          { type: 'string', required: true, message: this.$t('v_not_empty'), trigger: 'none' },
          {
            type: 'string',
            required: true,
            pattern: /\S/,
            message: this.$t('v_not_only_include_space'),
            trigger: 'none'
          }
        ],
        password: [
          { type: 'string', required: true, message: this.$t('v_not_empty'), trigger: 'none' },
          {
            type: 'string',
            required: true,
            pattern: /\S/,
            message: this.$t('v_not_only_include_space'),
            trigger: 'none'
          }
        ],
        verifyCode: this.needVerifyCode
          ? [
            { type: 'string', required: true, message: this.$t('v_not_empty'), trigger: 'none' },
            {
              type: 'string',
              required: true,
              pattern: /\S/,
              message: this.$t('v_not_only_include_space'),
              trigger: 'none'
            }
          ]
          : []
      };
    },
    showOneHik() {
      return getOneHikIdUpgradeStrategy(this.getCountryOrRegionIndex()) > 0;
    }
  },
  watch: {
    autoEmail(value) {
      if (value) {
        this.loginForm.email = value;
      }
    },
    'loginForm.email'(newVal, oldVal) {
      this.$refs.emailFormItem.resetValidate();
      if (newVal !== oldVal) {
        this.needVerifyCode = false;
        this.crossRegion = false;
      }
    },
    'loginForm.password'() {
      this.$refs.passwordFormItem.resetValidate();
    },
    'loginForm.verifyCode'() {
      this.$refs.verifyCodeFormItem.resetValidate();
    }
  },
  methods: {
    emailChange() {
      if (sessionStorage.getItem('updated_onehik')) {
        sessionStorage.removeItem('updated_onehik');
      }
    },
    toOneHikIdLogin() {
      this.toLogin('onehikid');
      this.$emit('close-card');
    },
    validate() {
      let result = false;
      this.$refs.loginForm.validate((valid) => {
        result = valid;
      });
      return result;
    },
    async onLogin(e) {
      if (sessionStorage.getItem('updated_onehik')) {
        this.loginForm.verifyCode = '';
        this.changeCode();
        this.$emit('close-card');
        return;
      }
      this.$bury.loginDialog(3);
      if (!this.validate()) {
        return;
      }
      this.$bury.login();
      e && e.target && e.target.blur();
      // 使用aes加密密码
      const aesKey = await cryptoAES.generateKey();
      const aesIv = cryptoAES.generateIv();
      const cryptedPassword = await cryptoAES.encrypt(this.loginForm.password, aesKey, aesIv);
      // 使用rsa加密aes秘钥和向量
      const [rsaData] = await this.$api.getRsaPublicKey();
      const aesKeyBuffer = await window.crypto.subtle.exportKey('raw', aesKey);
      const cryptedAESKey = await cryptoRSA.encrypt(aesKeyBuffer, rsaData.publicKey);
      const cryptedAESIv = await cryptoRSA.encrypt(aesIv, rsaData.publicKey);

      const params = {
        email: this.loginForm.email,
        password: cryptedPassword,
        aesKey: cryptedAESKey,
        aesIV: cryptedAESIv,
        loginType: 2,
        passwordType: 0,
        language: this.$i18n.locale === 'in' ? 'id' : this.$i18n.locale,
        rememberLogin: this.rememberLogin
      };
      if (this.needVerifyCode) {
        Object.assign(params, {
          verifyCode: this.loginForm.verifyCode
        });
      }
      if (this.crossRegion) {
        Object.assign(params, {
          lastSessionId: this.crossRegionData.sessionId
        });
      }

      const loading = this.$loading();
      let startTime = new Date().getTime();
      if (!params.terminalCode) {
        params.terminalCode = await handleFingerPrint('generate');
      }
      const [data, err, res] = await this.$api
        .login(params)
        .catch(() => {
          loading.close();
        })
        .finally(() => {
          let endTime = new Date().getTime();
          this.$bury.loginTimeCounting(endTime - startTime, 1);
        });
      //将onehikid存入store
      if (data) {
        sessionStorage.setItem('notLogUserInfo', JSON.stringify(data));
      }
      if (err) {
        loading.close();
        if (err.data && err.data.isCrossRegion) {
          this.crossRegion = true;
          this.crossRegionData = err.data;
        }
        if (err.errorCode === 'LAP002020') {
          window.location.href = err.data.targetUrl;
          return;
        }
        if (err?.errorCode === 'LAP004031' || err?.errorCode === 'LAP002041') {
          let adminEmail = '';
          for (let i = 0; i < err.data.adminInfo.length; i++) {
            adminEmail += (i > 0 ? '/' : '') + err.data.adminInfo[i].adminEmail;
          }
          this.$msgbox({
            dangerouslyUseHTMLString: true,
            message: `<span>${this.$t('HPP-web_LAP004031_msg').replace('{0}', `( ${adminEmail} )`)}</span>`,
            title: this.$t('error'),
            type: 'error'
          });
          return;
        }
        const upgradeStrategy = isAccountNeedUpgrade(this.getCountryOrRegionIndex());
        let errorText = '';
        switch (err.errorCode) {
          case 'LAP001003':
            errorText = this.$getErrorTip(err.errorCode, res);
            if (upgradeStrategy > 0) {
              errorText = this.$getErrorTip('LAP001003_', res);
            }
            if (err.data && err.data.remainLockAccountTimes) {
              errorText += ` ${this.$t('account_lock_time').replace('{0}', err.data.remainLockAccountTimes)}`;
            }
            break;
          case 'LAP001006':
            errorText = this.$t('login_verify_code_wrong');
            break;
          case 'LAP001008':
            errorText = this.$t('verify_code_space');
            break;
          case 'LAP001009':
            errorText = this.$t('verify_code_over');
            break;
          case 'LAP001011':
            errorText = this.$t('locked_ip');
            break;
          default:
            break;
        }
        if ('LAP001015' === err.errorCode) {
          this.$bury.resendModelShow();
          this.resendModel = true;
        } else if ('LAP001002' === err.errorCode) {
          //如果账号或密码输入错误会显示验证码，如果验证码过期会更新验证码
          this.loginForm.verifyCode = '';
          this.onVerifyCode();
          this.$msgbox({
            title: this.$t('error'),
            type: 'error',
            message: errorText
          });
        } else if ('LAP001003' === err.errorCode) {
          this.loginForm.verifyCode = '';
          this.onVerifyCode();
          this.$msgbox({
            title: this.$t('error'),
            type: 'error',
            message: errorText
          });
        } else if ('LAP001008' === err.errorCode || 'LAP001009' === err.errorCode || 'LAP001006' === err.errorCode) {
          this.loginForm.verifyCode = '';
          this.onVerifyCode();
          this.$msgbox({
            title: this.$t('error'),
            type: 'error',
            message: errorText
          });
        } else if ('LAP001029' === err.errorCode) {
          if (err.data && err.data.twoFactorId) {
            this.showAuthentication = true;
            await this.handleAuthentication();
            this.showAuthentication = false;
            //验证码完成后，继续登录
            // sessionStorage.setItem('accountEmailCache', data.email);
            window.location.href = `${window.location.origin}?hpcAfterLogin=true`;
          } else {
            this.$handleErrorAndTip(err.errorCode, res);
          }
        } else {
          if ('LAP002029' === err.errorCode) {
            sessionStorage.setItem('updated_onehik', true);
            this.$bury.loginDialog(5);
          }
          this.loginForm.verifyCode = '';
          this.onVerifyCode();
          this.$handleErrorAndTip(err.errorCode, res);
        }
      } else {
        sessionStorage.setItem('accountEmailCache', data.email);
        // data.portalAutoLoginId && cookie.set('portalAutoLoginId', data.portalAutoLoginId);
        // data.portalId && cookie.set('portalId', data.portalAutoLoginId);

        window.location.href = `${window.location.origin}?hpcAfterLogin=true`;
        // this.$emit('close-card');
        // loading.close();
      }
    },
    onVerifyCode() {
      this.needVerifyCode = true;
      this.changeCode();
    },
    toReset() {
      this.$bury.loginDialog(4);
      this.$emit('to-reset', this.loginForm.email);
    },
    changeCode() {
      if (this.crossRegion) {
        this.verifyImg = `/company/security/v1/crossregion/captcha/${this.crossRegionData.area}/${
          this.crossRegionData.sessionId
        }?${new Date().getTime()}`;
      } else {
        this.verifyImg = `/hpp/company/security/v1/captcha?${new Date().getTime()}`;
      }
    },
    async handleResend() {
      //埋点
      this.$bury.resendActiveEmail();
      const lang = parseInt(localStorage.getItem('hik_connect_pro_locale'), 10);
      const params = {
        email: this.loginForm.email,
        lang
      };
      const loading = this.$loading();
      const [data] = await this.$api.sendActiveAccountEmail(params).finally(() => loading.close());
      if (data) {
        this.$successMsg();
      }
    },
    resetForm() {
      this.$refs.loginForm.resetFields();
    },
    handleAuthentication() {
      return new Promise((resolve, reject) => {
        this.handleAuthenticationFn = {
          resolve,
          reject
        };
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.account-login {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 55px;
  ::v-deep .el-form-item__required {
    display: none !important;
  }
  .login-form {
    .login-code-pic {
      height: 40px;
      width: 124px;
      position: absolute;
      top: -10px;
      right: 0;
    }
  }
  .to-onehiklogin {
    margin-top: 13px;
    text-align: center;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 24px;
    font-weight: 400;
  }
  .back-nav {
    display: inline-block;
    /*margin-top: 42px;*/
    font-size: 14px;
    color: #484848;
    cursor: pointer;
  }
  /*::v-deep .entry-button {*/
  /*    margin: 39px 0 0 0;*/
  /*}*/
  ::v-deep .password-button {
    align-self: flex-start;
  }
  .auto-content {
    min-height: 50px;
    color: rgba(0, 0, 0, 0.7);
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0px;
    > div {
      margin-top: 10px;
    }
  }
  .entry-button-warp {
    flex-grow: 1;
    justify-content: space-around;
    display: flex;
    flex-direction: column;
  }
}
.login-resend-dialog {
  .dialog-info-position {
    position: absolute;
    left: 40px;
    top: 40px;
  }

  .dialog-content {
    position: absolute;
    left: 100px;
    top: 36px;
  }

  .dialog-title {
    opacity: 0.9;
    font-weight: 600;
    font-size: 16px;
    color: #000000;
    letter-spacing: 0;
  }

  .dialog-text {
    opacity: 0.7;
    font-size: 14px;
    color: #000000;
    letter-spacing: 0;
    line-height: 20px;

    .dialog-tip {
      margin-top: 8px;
      margin-bottom: 16px;
      width: 348px;
    }
  }

  .close-button {
    position: absolute;
    right: 32px;
    bottom: 32px;
    min-width: 180px;
  }
}
</style>
